export interface SegmentResult {
  dist: number;
  t: number;
  px: number;
  pz: number;
}

export interface PolylineResult {
  dist: number;
  arcLength: number;
  px: number;
  pz: number;
}

/** Distance from point (px,pz) to line segment (ax,az)→(bx,bz) */
export function distanceToSegment(
  px: number, pz: number,
  ax: number, az: number,
  bx: number, bz: number,
): SegmentResult {
  const dx = bx - ax;
  const dz = bz - az;
  const lenSq = dx * dx + dz * dz;

  let t = 0;
  if (lenSq > 1e-12) {
    t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / lenSq));
  }

  const cx = ax + t * dx;
  const cz = az + t * dz;
  const ddx = px - cx;
  const ddz = pz - cz;

  return { dist: Math.sqrt(ddx * ddx + ddz * ddz), t, px: cx, pz: cz };
}

/** Distance from point to polyline (Float64Array of [x0,z0, x1,z1, ...]) */
export function distanceToPolyline(
  px: number, pz: number,
  polyline: Float64Array,
): PolylineResult {
  const n = polyline.length / 2;
  let bestDist = Infinity;
  let bestArc = 0;
  let bestPx = 0;
  let bestPz = 0;
  let cumLen = 0;

  for (let i = 0; i < n - 1; i++) {
    const ax = polyline[i * 2], az = polyline[i * 2 + 1];
    const bx = polyline[(i + 1) * 2], bz = polyline[(i + 1) * 2 + 1];
    const segLen = Math.sqrt((bx - ax) ** 2 + (bz - az) ** 2);
    const r = distanceToSegment(px, pz, ax, az, bx, bz);

    if (r.dist < bestDist) {
      bestDist = r.dist;
      bestArc = cumLen + r.t * segLen;
      bestPx = r.px;
      bestPz = r.pz;
    }
    cumLen += segLen;
  }

  return { dist: bestDist, arcLength: bestArc, px: bestPx, pz: bestPz };
}

/** Ray-casting point-in-polygon test */
export function pointInPolygon(px: number, pz: number, polygon: Float64Array): boolean {
  const n = polygon.length / 2;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i * 2], zi = polygon[i * 2 + 1];
    const xj = polygon[j * 2], zj = polygon[j * 2 + 1];
    if ((zi > pz) !== (zj > pz) && px < (xj - xi) * (pz - zi) / (zj - zi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** Signed distance to polygon boundary (negative inside, positive outside) */
export function signedDistanceToPolygon(px: number, pz: number, polygon: Float64Array): number {
  const n = polygon.length / 2;
  let minDist = Infinity;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const r = distanceToSegment(
      px, pz,
      polygon[j * 2], polygon[j * 2 + 1],
      polygon[i * 2], polygon[i * 2 + 1],
    );
    if (r.dist < minDist) minDist = r.dist;
  }

  return pointInPolygon(px, pz, polygon) ? -minDist : minDist;
}
